import React, { useState, useRef, useEffect } from 'react';
import Button from '@mui/joy/Button';
import Menu from '@mui/joy/Menu';
import MenuItem from '@mui/joy/MenuItem';
import Checkbox from '@mui/joy/Checkbox';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import ListDivider from '@mui/joy/ListDivider';
import Box from '@mui/joy/Box';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'; // Optional: for an icon
import { MultiSelectCheckboxDropdownProps } from '../utils/types';
const MultiSelectCheckboxDropdown: React.FC<MultiSelectCheckboxDropdownProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = 'Select options',
  size = 'md',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionToggle = (value: string) => {
    const currentIndex = selectedValues.indexOf(value);
    const newSelectedValues = [...selectedValues];

    if (currentIndex === -1) {
      newSelectedValues.push(value);
    } else {
      newSelectedValues.splice(currentIndex, 1);
    }
    onChange(newSelectedValues);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]); // Deselect all
    } else {
      onChange(options.map(option => option.value)); // Select all
    }
  };

  const getButtonLabel = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === options.length) {
      return 'All selected';
    }
    if (selectedValues.length === 1) {
      const selectedOption = options.find(opt => opt.value === selectedValues[0]);
      return selectedOption ? selectedOption.label : placeholder;
    }
    return `${selectedValues.length} selected`;
  };

  // Close menu if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node) // Check if click is not within an open menu
      ) {
        // A bit more complex for nested menus, but for a simple menu this works
        // Check if the click target is part of the menu itself
        let targetElement = event.target as HTMLElement | null;
        let isClickInsideMenu = false;
        while(targetElement) {
            if (targetElement.getAttribute('role') === 'menu') {
                isClickInsideMenu = true;
                break;
            }
            targetElement = targetElement.parentElement;
        }
        if (!isClickInsideMenu) {
             handleClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorEl]);


  return (
    <FormControl size={size}>
      {label && <FormLabel>{label}</FormLabel>}
      <Button
        ref={buttonRef}
        variant="outlined"
        color="neutral"
        size={size}
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        aria-controls={isOpen ? 'multi-select-menu' : undefined}
        endDecorator={<ArrowDropDownIcon />}
        sx={{ justifyContent: 'space-between', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {getButtonLabel()}
      </Button>
      <Menu
        id="multi-select-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        placement="bottom-start"
        size={size}
        sx={{ minWidth: buttonRef.current?.offsetWidth, maxHeight: 300, overflowY: 'auto' }}
      >
        {options.length > 0 && (
            <>
                <MenuItem onClick={handleSelectAll}>
                    <Checkbox
                        size="sm"
                        checked={selectedValues.length === options.length && options.length > 0}
                        indeterminate={selectedValues.length > 0 && selectedValues.length < options.length}
                        sx={{ mr: 1 }}
                    />
                    {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
                </MenuItem>
                <ListDivider />
            </>
        )}
        {options.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleOptionToggle(option.value)}
            // selected={selectedValues.includes(option.value)} // 'selected' prop visually highlights the item
          >
            <Checkbox
              size="sm"
              checked={selectedValues.includes(option.value)}
              // onChange={(e) => { e.stopPropagation(); handleOptionToggle(option.value); }} // Can also handle here
              sx={{ mr: 1 }}
            />
            {option.label}
          </MenuItem>
        ))}
        {options.length === 0 && <MenuItem disabled>No options available</MenuItem>}
      </Menu>
    </FormControl>
  );
};

export default MultiSelectCheckboxDropdown;